/**
 * Display formatters shared across portal resource pages.
 *
 * Pure, dependency-free helpers for rendering the worker's wire values (ISO-8601
 * UTC timestamps, snake_case enum strings, numbers) as human-readable text. No
 * I/O, no React — safe to use anywhere. Locale-aware via `Intl`.
 */

/** Parse an ISO-8601 string into a `Date`, or `null` if absent/invalid. */
function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format an ISO timestamp as a localized date-time (e.g. `Jun 24, 2026, 10:00`).
 * Returns an em dash for null/invalid input so tables never show `Invalid Date`.
 */
export function formatDateTime(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Format an ISO timestamp as a localized date only (e.g. `Jun 24, 2026`). */
export function formatDate(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

const RELATIVE_DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'seconds' },
  { amount: 60, unit: 'minutes' },
  { amount: 24, unit: 'hours' },
  { amount: 7, unit: 'days' },
  { amount: 4.34524, unit: 'weeks' },
  { amount: 12, unit: 'months' },
  { amount: Number.POSITIVE_INFINITY, unit: 'years' },
];

/**
 * Format an ISO timestamp relative to now (e.g. `3 minutes ago`, `in 2 days`).
 * Returns an em dash for null/invalid input.
 */
export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return '—';

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  let duration = (date.getTime() - now.getTime()) / 1000; // seconds, signed

  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return formatter.format(Math.round(duration), 'years');
}

/**
 * Humanize a snake_case / kebab-case enum value into Title Case
 * (`in_progress` → `In Progress`, `agent-runs` → `Agent Runs`). Empty/nullish
 * input yields an em dash.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/** Format a number with thousands separators, or an em dash for null. */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat().format(value);
}

/**
 * Format a 0..1 confidence/ratio as a percentage (e.g. `0.82` → `82%`). Clamps
 * out-of-range input; null yields an em dash.
 */
export function formatPercent(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const clamped = Math.min(1, Math.max(0, value));
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(clamped);
}

/**
 * Compute key-result / objective progress as a 0..100 number from a current and
 * target value. Returns 0 when target is missing or non-positive (avoids
 * divide-by-zero); clamps to [0, 100].
 */
export function progressPercent(
  current: number | null | undefined,
  target: number | null | undefined,
): number {
  if (!target || target <= 0 || current === null || current === undefined) return 0;
  const pct = (current / target) * 100;
  if (Number.isNaN(pct)) return 0;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

/** Truncate a string to `max` chars, appending an ellipsis when cut. */
export function truncate(value: string | null | undefined, max = 80): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}
